export class Option {
    constructor(
        public lookup: string,
        public category: string,
        public key: string
    ) {
        if (Option.locked) {
            throw new Error('Use Option.fromParts to get an option.');
        }
    }

    private static locked = true;
    private static knownOptions: Record<string, Option> = {};

    private static create(
        lookup: string,
        category: string,
        key: string
    ): Option {
        let knownOption = Option.knownOptions[lookup];
        if (!knownOption) {
            Option.locked = false;
            try {
                knownOption = Object.freeze(new Option(lookup, category, key));
            } finally {
                Option.locked = true;
            }
            Option.knownOptions[lookup] = knownOption;
        }
        return knownOption;
    }

    public static fromLookup(lookup: string) {
        const prefix = lookup.indexOf('/');
        const category = decodeURIComponent(
            prefix > -1 ? lookup.substring(0, prefix) : lookup
        );
        const key = decodeURIComponent(
            prefix > -1 ? lookup.substring(prefix + 1) : ''
        );
        return Option.create(lookup, category, key);
    }

    public static fromParts(category: string, key: string) {
        const lookup = `${encodeURIComponent(category)}/${encodeURIComponent(
            key
        )}`;
        return Option.create(lookup, category, key);
    }
}

export const WellknownOption = {
    MAX_AI_REQUEST: Option.fromLookup('ai/max_requests_per_month'),
    AI_REQUEST: Option.fromLookup('ai/request'),
    EXT_API: Option.fromLookup('extApi/enable'),
    FILE_PAUSED: Option.fromLookup('file/paused'),
    MOSAIC_ENABLED: Option.fromLookup('mosaic/enable'),
    TASK_PAUSED: Option.fromLookup('task/paused'),
    FILE_PAUSED_MAINTENANCE: Option.fromLookup('file/paused-maintenance'),
    TASK_PAUSED_MAINTENANCE: Option.fromLookup('task/paused-maintenance'),
} as const;

export enum OptionCause {
    SYS_OVERRIDE = 'sys-override',
    SYS_FALLBACK = 'sys-fallback',
    ENV_OVERRIDE = 'env-override',
    ENV_FALLBACK = 'env-fallback',
}

export type OptionType = 'boolean' | 'uint' | 'int' | undefined;

export interface SystemOptionEntry {
    lookup: string;
    label?: string;
    type?: OptionType;
    isWellKnown: boolean;
    value: any;
}

export interface SystemOptionsEntry {
    lookup: string;
    label?: string;
    type?: OptionType;
    isWellKnown: boolean;
    values: {
        [key in OptionCause]: any;
    };
}

export enum SystemOptionType {
    Fallback = 'fallback',
    Override = 'override',
}

export interface SystemInfo {
    version: string;
    maintenance: boolean;
    paused: {
        ai: boolean;
        file: boolean;
    };
    cognito: {
        region: string;
        userPoolId: string;
    };
}
